import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';

const bgLogger = logger.create('RotatingBackground');

const SEVEN_WONDERS_IMAGES = [
  {
    url: 'https://images.unsplash.com/photo-1571822325911-c01620a65e86',
    name: 'Great Wall of China',
    location: 'China'
  },
  {
    url: 'https://images.unsplash.com/photo-1712323028707-6e59c3d2271a',
    name: 'Petra',
    location: 'Jordan'
  },
  {
    url: 'https://images.unsplash.com/photo-1715528892617-fab2cd0ce0e7',
    name: 'Christ the Redeemer',
    location: 'Brazil'
  },
  {
    url: 'https://images.unsplash.com/photo-1580619305218-8423a7ef79b4',
    name: 'Machu Picchu',
    location: 'Peru'
  },
  {
    url: 'https://images.unsplash.com/photo-1661629705151-bd825e2ef98f',
    name: 'Chichen Itza',
    location: 'Mexico'
  },
  {
    url: 'https://images.unsplash.com/photo-1658930204311-7a51f5ddc4ee',
    name: 'Roman Colosseum',
    location: 'Italy'
  },
  {
    url: 'https://images.unsplash.com/photo-1564507592333-c60657eea523',
    name: 'Taj Mahal',
    location: 'India'
  }
];

const RotatingBackground = ({ children, interval = 8000, showCaption = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());

  useEffect(() => {
    // Preload all images
    let loadedCount = 0;
    SEVEN_WONDERS_IMAGES.forEach((image, index) => {
      const img = new Image();
      img.src = `${image.url}?w=1920&q=80`;
      img.onload = () => {
        loadedCount++;
        setLoadedImages(prev => new Set(prev).add(index));
        if (loadedCount === 1) {
          setIsLoaded(true); // Show first image once it loads
        }
      };
      img.onerror = () => {
        bgLogger.error(`Failed to load image: ${image.name}`);
        loadedCount++;
        if (loadedCount === 1) {
          setIsLoaded(true); // Still show content even if first image fails
        }
      };
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % SEVEN_WONDERS_IMAGES.length);
    }, interval);

    return () => clearInterval(timer);
  }, [interval]);

  const currentImage = SEVEN_WONDERS_IMAGES[currentIndex];

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Images with Crossfade */}
      <div className="fixed inset-0 z-0 bg-slate-900">
        {SEVEN_WONDERS_IMAGES.map((image, index) => (
          <div
            key={image.name}
            className="absolute inset-0 ease-in-out"
            style={{
              opacity: index === currentIndex && loadedImages.has(index) ? 1 : 0,
              backgroundImage: `url(${image.url}?w=1920&q=80)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              transition: 'opacity 2000ms ease-in-out',
              willChange: 'opacity'
            }}
          />
        ))}
        
        {/* Gradient Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 dark:from-black/70 dark:via-black/50 dark:to-black/80" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>

      {/* Optional Caption */}
      {showCaption && (
        <div className="fixed right-6 z-20 bg-black/50 backdrop-blur-md px-4 py-2 rounded-lg border border-white/20" style={{ bottom: 'calc(max(env(safe-area-inset-bottom, 0px), 24px) + 24px)' }}>
          <div className="text-white text-sm font-medium">{currentImage.name}</div>
          <div className="text-slate-300 text-xs">{currentImage.location}</div>
        </div>
      )}

      {/* Progress Indicators */}
      <div className="fixed left-1/2 transform -translate-x-1/2 z-20 flex items-center" style={{ gap: '6px', bottom: 'max(env(safe-area-inset-bottom, 0px), 24px)' }}>
        {SEVEN_WONDERS_IMAGES.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            data-testid={`bg-dot-${index}`}
            style={{
              width: index === currentIndex ? '20px' : '5px',
              height: '5px',
              minWidth: index === currentIndex ? '20px' : '5px',
              maxWidth: index === currentIndex ? '20px' : '5px',
              minHeight: '5px',
              maxHeight: '5px',
              borderRadius: '9999px',
              backgroundColor: index === currentIndex ? '#ffffff' : 'rgba(255,255,255,0.4)',
              transition: 'all 0.3s ease',
              border: 'none',
              padding: '0',
              margin: '0',
              cursor: 'pointer',
              display: 'block',
              boxSizing: 'border-box',
              WebkitAppearance: 'none',
              appearance: 'none'
            }}
            aria-label={`Go to ${SEVEN_WONDERS_IMAGES[index].name}`}
          />
        ))}
      </div>
    </div>
  );
};

export default RotatingBackground;
